import { NormalizedRideSchema } from '../../../renderer/src/contracts';
import { API, apiRequest } from './api-client';
import { localCapabilities, localVideoConfiguration, type VideoJob } from './local-video';
import { rememberAccountRide } from './account-import';
import { videoConfigurationDraft } from '../studio/video-configuration-draft';
import type { Ride } from '../domain/ride';

/** Uses the same private service as My videos; opening a configuration never submits a render. */
export async function loadVideoConfiguration(id: string) {
  const selected = new URLSearchParams(location.search).get('destination');
  const local = selected === 'local' || (selected !== 'remote' && (await localCapabilities()).localRendering);
  const prefix = selected === 'remote' ? '/api/local/remote' : '';
  const configuration = local ? await localVideoConfiguration(id)
    : await apiRequest<{ ride: unknown; job: VideoJob }>(`${prefix}${API}/render-jobs/${encodeURIComponent(id)}/configuration`);
  const parsed = NormalizedRideSchema.safeParse(configuration.ride);
  if (!parsed.success) throw new Error('The saved trace could not be read. Open the original trace file to continue.');
  const source = parsed.data;
  if (source.source === 'synthetic') throw new Error('The recorded source trace is unavailable.');
  // Each video retains its own edits even when several videos use the same recording.
  const { sourceFileName, recordingDevice, ...normalized } = source;
  const ride: Ride = { ...normalized, title: configuration.job.title, sourceFingerprint: `saved-video:${id}`,
    points: source.points.map(({ segmentStart, ...point }) => segmentStart === undefined ? point : { ...point, segmentStart }),
    ...(sourceFileName === undefined ? {} : { sourceFileName }), ...(recordingDevice === undefined ? {} : { recordingDevice }) };
  let draft;
  try { draft = videoConfigurationDraft(ride, configuration.job); }
  catch { throw new Error('The saved video settings could not be restored. Open the original trace file to continue.'); }
  if (!local && configuration.job.rideId) rememberAccountRide(ride.id, configuration.job.rideId);
  return { ride, draft };
}
